import { useParams, useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Box,
  Card,
  Typography,
  Chip,
  IconButton,
  Button,
  Skeleton,
  Avatar,
  DialogContent,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import EditIcon from '@mui/icons-material/Edit';

import CustomTabs from 'src/components/shared/CustomTabs/CustomTabs';
import { ReactComponent as Man } from 'src/assets/images/woman.svg';
import { ReactComponent as Calender } from 'src/assets/images/calendar.svg';
import { ReactComponent as Weight } from 'src/assets/images/weight.svg';
import { ReactComponent as Height } from 'src/assets/images/ruler.svg';

import History from './Histroy/History';
import CaseHistory from './CaseHistory/CaseHistory';
import { getActiveCase } from 'src/modules/app/api/appApi';
import Appointments from './Appointments/Appointments';
import Notes from './Notes/Notes';
import Prescription from './Prescription/Prescription';
import FollowUps from './FollowUp/FollowUps';
import Payment from './Payments/Payment';
import Treatments from './Treatments/Treatments';
import Message from './Message/Message';
import Reports from './Reports/Reports';
import DialogBox from 'src/components/shared/Dialog/DialogBox';
import EditProfile from './Forms/EditProfile';
import {
  getAppointmentRequest,
  getAppointmentByIdRequest,
  getPatientByIdRequest,
  getPatientInfoByIdRequest,
  getTagByPatientIdRequest,
  getAreaByPatientIdRequest,
  updateRecordStatusRequest,
  fetchPatientsRequest,
  SelectedPatient,
} from '../../store/appActions';
import NotesTabForm from './Forms/NotesTabForm';
import AppointmentTabForm from './Forms/AppointmentTabForm';
import FollowUpTabForm from './Forms/FollowUpTabForm';
import PaymentTabForm from './Forms/PaymentTabForm';
import PrescriptionTabForm from './Forms/PrescriptionTabForm';
import ReportTabForm from './Forms/ReportTabForm';
import MessageTabForm from './Forms/MessageTabForm';
import TreatmentTabForm from './Forms/TreatmentTabForm';
import storage from 'src/utils/storageUtils';
import PatientInfo from './PatientInfoDrawer/PatientInfo';
import DeleteDialog from '../DeleteDialog';
import ButtonComponent from 'src/components/shared/Button';
import useAppointmentDetails from '../UseAppointmentDetails';
import { StatusHeader, PatientName, PatientNameContainer, TwoBtns } from './Style';

function Patients() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { appointmentId: routeApptId, patientId: routePatientId } = useParams();
  const [appointments, setAppointments] = useState([]);
  const [patientsList, setPatientsList] = useState([]);
  const [navigationDirection, setNavigationDirection] = useState(null);

  const [shouldRefresh, setShouldRefresh] = useState(false);
  const handleSuccess = () => {
    setShouldRefresh((prev) => !prev);
    handleAddItemClose();
  };

  const [statusSelectOpen, setStatusSelectOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const [openComplete, setOpenComplete] = useState(false);
  const [selectedMenuLabel, setSelectedMenuLabel] = useState('Add Item');
  const [addItemOpen, setAddItemOpen] = useState(false);

  const handleAddItemClose = () => {
    setAddItemOpen(false);
    setSelectedMenuLabel('Add Item');
  };

  const isDark = theme.palette.mode === 'dark';

  const tabsData = [
    { label: 'Appointments', component: <Appointments shouldRefresh={shouldRefresh} /> },
    { label: 'History', component: <History /> },
    { label: 'Case History', component: <CaseHistory /> },
    { label: 'Prescriptions', component: <Prescription shouldRefresh={shouldRefresh} /> },
    { label: 'Follow-ups', component: <FollowUps shouldRefresh={shouldRefresh} /> },
    { label: 'Treatment', component: <Treatments shouldRefresh={shouldRefresh} /> },
    { label: 'Reports', component: <Reports shouldRefresh={shouldRefresh} /> },
    { label: 'Payments', component: <Payment shouldRefresh={shouldRefresh} /> },
    { label: 'Message', component: <Message shouldRefresh={shouldRefresh} /> },
  ];

  const formComponents = {
    'Add Appointment': <AppointmentTabForm onClose={handleAddItemClose} fetchAppointments={handleSuccess} />,
    'Add Payment': <PaymentTabForm onClose={handleAddItemClose} fetchPayments={handleSuccess} />,
    'Add Report': <ReportTabForm onClose={handleAddItemClose} fetchReports={handleSuccess} />,
  };

  const [selectedTab, setSelectedTab] = useState(0);

  const patientData = useSelector((state) => state.app.patientById);
  const patientInfoData = useSelector((state) => state.app.patientInfoById);
  const TagData = useSelector((state) => state.app.TagByPatientId);
  const TagArray = TagData?.payload?.data?.[0] || {};
  const patientInfoDetails = patientInfoData?.payload?.data?.[0] || {};

  const patientDetails =
    patientData?.payload?.data?.patient ||
    patientData?.payload?.data ||
    patientData?.data?.patient ||
    patientData?.patient;

  const { PatientId: hookPatientId, user, clinicId, appointmentDetails } = useAppointmentDetails();
  const PatientId = routePatientId || hookPatientId;
  const appointmentId = routeApptId || appointmentDetails?.id || appointmentDetails?._id || null;

  const isLoading = useSelector((state) => state.app.isLoading);
  const [activeCaseInfo, setActiveCaseInfo] = useState(null);

  // Fetch Active Case
  useEffect(() => {
    const fetchActiveCase = async () => {
      if (PatientId && user && clinicId) {
        try {
          const response = await getActiveCase({ patient: PatientId, doctor: user, clinic: clinicId });
          if (response && response.activeCase) {
            setActiveCaseInfo(response.activeCase);
          } else {
            setActiveCaseInfo(null);
          }
        } catch (err) {
          console.error('Error fetching active case:', err);
        }
      }
    };
    fetchActiveCase();
  }, [PatientId, user, clinicId, shouldRefresh]);

  // Fetch Patient Details, Vitals, Tags, Area
  useEffect(() => {
    if (PatientId) {
      dispatch(getPatientByIdRequest(PatientId));
      dispatch(getPatientInfoByIdRequest(PatientId));
      dispatch(getTagByPatientIdRequest(PatientId));
      dispatch(getAreaByPatientIdRequest(PatientId));
    }
    if (appointmentId) {
      dispatch(getAppointmentByIdRequest(appointmentId));
    }
  }, [PatientId, appointmentId, dispatch, shouldRefresh]);

  // Fetch appointments list for pagination
  useEffect(() => {
    if (location.state?.appointmentList && location.state.appointmentList.length > 0) {
      setAppointments(location.state.appointmentList);
      return;
    }

    const fetchAppointments = async () => {
      const token = storage.get('TOKEN');
      const params = {
        clinicId: clinicId,
        limit: 100,
      };
      try {
        const response = await new Promise((resolve, reject) =>
          dispatch(getAppointmentRequest({ token, params }, resolve, reject))
        );

        if (response && response.data) {
          const filteredData = response.data.filter((appointment) =>
            ['In Queue', 'Attending Now', 'Scheduled', 'Treatment Scheduled', 'Complete'].includes(
              appointment.recordStatus
            )
          );
          setAppointments(filteredData);
        }
      } catch (err) {
        console.error('Error fetching appointments list:', err);
      }
    };

    if (clinicId) {
      fetchAppointments();
    }
  }, [dispatch, appointmentId, location.state?.appointmentList, clinicId]);

  // Fetch patients list for fallback pagination
  useEffect(() => {
    if (location.state?.patientList && location.state.patientList.length > 0) {
      setPatientsList(location.state.patientList);
      return;
    }
    const fetchPatientList = async () => {
      const token = storage.get('TOKEN');
      try {
        const response = await new Promise((resolve, reject) =>
          dispatch(fetchPatientsRequest(token, 1, 50, resolve, reject))
        );
        if (response?.data && Array.isArray(response.data)) {
          setPatientsList(response.data);
        }
      } catch (e) {
        console.error('Error fetching patient list for pagination:', e);
      }
    };
    fetchPatientList();
  }, [dispatch, location.state?.patientList]);

  const currentIndex = appointmentId ? appointments.findIndex((a) => a.id === appointmentId) : -1;
  const patientIndex = PatientId ? patientsList.findIndex((p) => (p.id || p._id) === PatientId) : -1;

  const isFirst = appointmentId
    ? currentIndex <= 0 && currentIndex !== -1
    : patientIndex <= 0 && patientIndex !== -1;

  const isLast = appointmentId
    ? currentIndex !== -1 && currentIndex === appointments.length - 1
    : patientIndex !== -1 && patientIndex === patientsList.length - 1;

  const handleNext = () => {
    if (appointmentId && appointments.length > 0 && !isLast) {
      const currentStatus = appointmentDetails?.recordStatus;
      const nextId = appointments[currentIndex + 1]?.id;

      if (currentStatus === 'Attending Now') {
        setNavigationDirection('next');
        setOpenComplete(true);
      } else if (nextId) {
        navigate(`/appointment/Patients/${nextId}`, { state: { appointmentList: appointments } });
      }
    } else if (patientsList.length > 0 && !isLast) {
      const nextP = patientsList[patientIndex + 1];
      if (nextP) {
        const nextPId = nextP.id || nextP._id;
        storage.set('PATIENT_ID', nextPId);
        storage.set('PATIENT_NAME', nextP.fullName || nextP.name || '');
        dispatch(
          SelectedPatient({
            patientName: nextP.fullName || nextP.name,
            patientId: nextPId,
            id: nextPId,
            patient: nextP,
          })
        );
        navigate(`/viewprofile/${nextPId}`, { state: { patientList: patientsList } });
      }
    }
  };

  const handlePrev = () => {
    if (appointmentId && appointments.length > 0 && !isFirst) {
      const prevId = appointments[currentIndex - 1]?.id;
      if (prevId) {
        navigate(`/appointment/Patients/${prevId}`, { state: { appointmentList: appointments } });
      }
    } else if (patientsList.length > 0 && !isFirst) {
      const prevP = patientsList[patientIndex - 1];
      if (prevP) {
        const prevPId = prevP.id || prevP._id;
        storage.set('PATIENT_ID', prevPId);
        storage.set('PATIENT_NAME', prevP.fullName || prevP.name || '');
        dispatch(
          SelectedPatient({
            patientName: prevP.fullName || prevP.name,
            patientId: prevPId,
            id: prevPId,
            patient: prevP,
          })
        );
        navigate(`/viewprofile/${prevPId}`, { state: { patientList: patientsList } });
      }
    }
  };

  const handleCompleteClose = () => {
    setOpenComplete(false);
  };


  const updateAppointmentStatus = async (targetApptId, status, onSuccess) => {
    const updatedData = { recordStatus: status };
    try {
      const response = await new Promise((resolve, reject) => {
        dispatch(updateRecordStatusRequest(targetApptId, updatedData, resolve, reject));
      });
      if (response) {
        setAppointments((prev) =>
          prev.map((a) => (a.id === targetApptId ? { ...a, recordStatus: status } : a))
        );
        if (onSuccess) onSuccess();
      }
    } catch (error) {
      console.error(`Failed to update status to ${status}:`, error);
    }
  };

  const handleCompleteAppointment = (targetApptId) => {
    updateAppointmentStatus(targetApptId, 'Complete', () => {
      handleCompleteClose();
      const currIdx = appointments.findIndex((a) => a.id === targetApptId);
      const nextId = appointments[currIdx + 1]?.id;
      const prevId = appointments[currIdx - 1]?.id;

      if (navigationDirection === 'next' && nextId) {
        navigate(`/appointment/Patients/${nextId}`, { state: { appointmentList: appointments } });
      } else if (navigationDirection === 'prev' && prevId) {
        navigate(`/appointment/Patients/${prevId}`, { state: { appointmentList: appointments } });
      }
      setNavigationDirection(null);
    });
  };

  const goToNextAppointment = () => {
    const currIdx = appointments.findIndex((a) => a.id === appointmentId);
    const nextId = appointments[currIdx + 1]?.id;
    const prevId = appointments[currIdx - 1]?.id;

    if (navigationDirection === 'next' && nextId) {
      navigate(`/appointment/Patients/${nextId}`, { state: { appointmentList: appointments } });
    } else if (navigationDirection === 'prev' && prevId) {
      navigate(`/appointment/Patients/${prevId}`, { state: { appointmentList: appointments } });
    }
    setNavigationDirection(null);
  };

  const getDetailedAgeString = (p) => {
    if (p?.detailedAge?.ageString) return p.detailedAge.ageString;
    if (p?.dateOfBirth) {
      const birth = dayjs(p.dateOfBirth);
      const now = dayjs();
      const years = now.diff(birth, 'year');
      const months = now.diff(birth.add(years, 'year'), 'month');
      const days = now.diff(birth.add(years, 'year').add(months, 'month'), 'day');
      return `${years} ${years === 1 ? 'Year' : 'Years'} ${months} ${months === 1 ? 'Month' : 'Months'} ${days} ${days === 1 ? 'Day' : 'Days'}`;
    }
    if (p?.age) {
      return `${p.age} ${p.age === 1 ? 'Year' : 'Years'}`;
    }
    return 'N/A';
  };

  const displayName = patientDetails?.fullName || patientDetails?.name || storage.get('PATIENT_NAME') || 'Patient';
  const initialChar = displayName ? displayName.charAt(0).toUpperCase() : 'P';
  const patientIdFormatted = patientDetails?.patientId || (PatientId ? `MYC-${String(PatientId).slice(-5).toUpperCase()}` : 'MYC-N/A');

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2.5, pb: 4 }}>
      {/* Top Bar with Back Button */}
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            color: theme.palette.text.primary,
            fontSize: '14px',
            borderRadius: '8px',
            px: 1.5,
            py: 0.5,
            bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
            border: `1px solid ${theme.palette.divider}`,
            '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)' },
          }}
        >
          Back
        </Button>
      </Box>

      {/* Patient Header Card */}
      <Card
        sx={{
          borderRadius: '16px',
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: isDark ? '0 4px 20px rgba(0,0,0,0.4)' : '0 4px 16px rgba(0,0,0,0.03)',
          bgcolor: theme.palette.background.paper,
          p: { xs: 2, md: 2.5 },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          {/* Part 1: Avatar + Details */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flex: 1, minWidth: '300px' }}>
            {isLoading ? (
              <Skeleton variant="rounded" width={64} height={64} sx={{ borderRadius: '14px' }} />
            ) : (
              <Avatar
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '14px',
                  bgcolor: isDark ? 'rgba(99, 102, 241, 0.2)' : '#EEF2FF',
                  color: theme.palette.primary.main,
                  fontSize: '24px',
                  fontWeight: 700,
                }}
              >
                {initialChar}
              </Avatar>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {/* Line 1: Name, ID, Active Case, Tags */}
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
                {isLoading ? (
                  <Skeleton width={200} height={32} />
                ) : (
                  <>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.text.primary, fontSize: '20px' }}>
                      {displayName}
                    </Typography>

                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 600, fontSize: '13px' }}>
                      ID: {patientIdFormatted}
                    </Typography>

                    {activeCaseInfo ? (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          px: '10px',
                          py: '3px',
                          borderRadius: '100px',
                          backgroundColor: 'rgba(76, 175, 80, 0.1)',
                          border: '1px solid rgba(76, 175, 80, 0.3)',
                        }}
                      >
                        <Box sx={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#4CAF50' }} />
                        <Typography variant="caption" sx={{ color: '#2E7D32', fontWeight: 700, fontSize: '11px' }}>
                          Active Case: {activeCaseInfo.caseNumber} (Valid till:{' '}
                          {dayjs(activeCaseInfo.validityEndDate).format('DD-MMM')})
                        </Typography>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          px: '10px',
                          py: '3px',
                          borderRadius: '100px',
                          backgroundColor: 'rgba(244, 67, 54, 0.08)',
                          border: '1px solid rgba(244, 67, 54, 0.25)',
                        }}
                      >
                        <Box sx={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#F44336' }} />
                        <Typography variant="caption" sx={{ color: '#D32F2F', fontWeight: 700, fontSize: '11px' }}>
                          No Active Case
                        </Typography>
                      </Box>
                    )}

                    <Chip
                      label={
                        Array.isArray(TagArray?.tag) && TagArray.tag.length > 0
                          ? TagArray.tag.join(', ')
                          : 'No tags available'
                      }
                      size="small"
                      sx={{
                        bgcolor: '#F1F5F9',
                        color: '#64748B',
                        fontWeight: 600,
                        fontSize: '11px',
                        borderRadius: '100px',
                        height: '24px',
                      }}
                    />
                  </>
                )}
              </Box>

              {/* Line 2: Gender, Age, Vitals, Pencil */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 2,
                  color: theme.palette.text.secondary,
                  fontSize: '13px',
                }}
              >
                {isLoading ? (
                  <Skeleton width={350} height={20} />
                ) : (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Man width="15px" height="15px" />
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '13px', fontWeight: 500 }}>
                        {patientDetails?.gender
                          ? patientDetails.gender.charAt(0).toUpperCase() +
                            patientDetails.gender.slice(1).toLowerCase()
                          : 'Male'}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Calender width="15px" height="15px" />
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '13px', fontWeight: 500 }}>
                        {getDetailedAgeString(patientDetails)}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} title="Weight">
                      <Weight width="15px" height="15px" />
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '13px', fontWeight: 500 }}>
                        {patientInfoDetails?.weight ? `${patientInfoDetails.weight} kg` : 'N/A'}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} title="Height">
                      <Height width="15px" height="15px" />
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '13px', fontWeight: 500 }}>
                        {patientInfoDetails?.height ? `${patientInfoDetails.height} cm` : 'N/A'}
                      </Typography>
                    </Box>

                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '13px', fontWeight: 500 }}>
                      P: {patientInfoDetails?.pulseRate ? `${patientInfoDetails.pulseRate} bpm` : 'N/A'}
                    </Typography>

                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '13px', fontWeight: 500 }}>
                      O2: {patientInfoDetails?.spo2 ? `${patientInfoDetails.spo2}%` : 'N/A'}
                    </Typography>

                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '13px', fontWeight: 500 }}>
                      T: {patientInfoDetails?.temperature ? `${patientInfoDetails.temperature}°F` : 'N/A'}
                    </Typography>

                    <IconButton
                      size="small"
                      onClick={handleOpen}
                      sx={{
                        bgcolor: isDark ? 'rgba(99, 102, 241, 0.2)' : '#EEF2FF',
                        color: theme.palette.primary.main,
                        width: 26,
                        height: 26,
                        p: 0.5,
                        '&:hover': { bgcolor: isDark ? 'rgba(99, 102, 241, 0.3)' : '#E0E7FF' },
                      }}
                    >
                      <EditIcon sx={{ fontSize: '14px' }} />
                    </IconButton>
                  </>
                )}
              </Box>
            </Box>
          </Box>

          {/* Part 2: Prev / Next Navigation */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<NavigateBeforeIcon />}
              onClick={handlePrev}
              disabled={isFirst}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                borderColor: theme.palette.divider,
                color: theme.palette.text.primary,
                fontSize: '13px',
                px: 1.5,
              }}
            >
              Prev
            </Button>
            <Button
              variant="outlined"
              size="small"
              endIcon={<NavigateNextIcon />}
              onClick={handleNext}
              disabled={isLast}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                borderColor: theme.palette.divider,
                color: theme.palette.text.primary,
                fontSize: '13px',
                px: 1.5,
              }}
            >
              Next
            </Button>
          </Box>
        </Box>
      </Card>

      {/* Tabs & Content Card */}
      <Card
        sx={{
          borderRadius: '16px',
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: isDark ? '0 4px 20px rgba(0,0,0,0.4)' : '0 4px 16px rgba(0,0,0,0.03)',
          bgcolor: theme.palette.background.paper,
          overflow: 'hidden',
        }}
      >
        {/* Underline Tabs Navigation */}
        <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}`, px: 2, pt: 1, bgcolor: isDark ? 'rgba(255,255,255,0.02)' : '#F8FAFC' }}>
          <CustomTabs
            value={selectedTab}
            tabs={tabsData}
            onChange={(index) => setSelectedTab(index)}
            sx={{
              tabStyles: {
                color: theme.palette.text.secondary,
                selectedColor: theme.palette.primary.main,
                height: '46px',
                fontSize: '14px',
                fontWeight: 600,
                textTransform: 'none',
                minWidth: 'auto',
                px: 2,
              },
              indicatorStyles: {
                color: theme.palette.primary.main,
                height: '3px',
              },
            }}
          />
        </Box>

        {/* Tab Content Body */}
        <Box sx={{ p: { xs: 2, sm: 3 }, minHeight: '420px', bgcolor: theme.palette.background.paper }}>
          {tabsData[selectedTab]?.component}
        </Box>
      </Card>

      {/* Edit Profile & Vitals Modal */}
      <DialogBox
        open={open}
        PaperProps={{
          sx: {
            maxWidth: 'none',
          },
        }}
      >
        <EditProfile onClose={handleClose} />
      </DialogBox>

      {/* Add Item Forms */}
      <DialogBox
        open={addItemOpen}
        onClose={handleAddItemClose}
        PaperProps={{
          sx: {
            margin: '0 !important',
          },
        }}
      >
        <Box key={selectedMenuLabel}>{formComponents[selectedMenuLabel]}</Box>
      </DialogBox>

      <PatientInfo open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      {/* Appointment Complete Confirmation Dialog */}
      <DeleteDialog
        open={openComplete}
        onClose={handleCompleteClose}
        onCancel={() => {
          handleCompleteClose();
          setStatusSelectOpen(true);
        }}
        onConfirm={handleCompleteAppointment}
        idToDelete={appointmentId}
        title="Complete Appointment?"
        message={`Would you like to complete the appointment of ${displayName} before moving to the next?`}
        confirmText="OK"
      />

      {/* Status Selection Dialog */}
      <DialogBox open={statusSelectOpen} onClose={() => setStatusSelectOpen(false)}>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 3 }}>
          <StatusHeader>What status would you like to keep for</StatusHeader>
          <PatientNameContainer sx={{ mt: 1, mb: 2 }}>
            <PatientName>{displayName}</PatientName>
          </PatientNameContainer>

          <TwoBtns sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <ButtonComponent
              onClick={() => {
                updateAppointmentStatus(appointmentId, 'Attending Now', () => setStatusSelectOpen(false));
                goToNextAppointment();
              }}
            >
              Attending Now
            </ButtonComponent>
            <ButtonComponent
              onClick={() => {
                updateAppointmentStatus(appointmentId, 'In Queue', () => setStatusSelectOpen(false));
                goToNextAppointment();
              }}
            >
              In Queue
            </ButtonComponent>
          </TwoBtns>
        </DialogContent>
      </DialogBox>
    </Box>
  );
}

export default Patients;
